// ======================================================================
// 📁 File: src/modules/ppe/pages/material/boughtout/BoughtOutDrawer.jsx
// 📝 Purpose:
//   Bought-Out Item Master – Create / Edit Drawer
//   - Code, Name, Base UOM, Issue UOM, Supplier Reference
//   - Dropdown from UOM master
//   - POST / PUT API integration
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function BoughtOutDrawer({
  open,
  mode = "create",
  record = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    code: "",
    name: "",
    base_uom_id: "",
    issue_uom_id: "",
    supplier_reference: "",

    // --------------------------------------------------------------
    // ADDED: Tax Classification field in form state
    // --------------------------------------------------------------
    tax_classification_id: "",
  });

  const [uoms, setUoms] = useState([]);

  // --------------------------------------------------------------
  // ADDED: State to hold tax classifications
  // --------------------------------------------------------------
  const [taxList, setTaxList] = useState([]);

  const [loadingUoms, setLoadingUoms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ------------------------------------------------------------
  // CHANGED: Load both UOM and Tax Classification
  // ------------------------------------------------------------
  useEffect(() => {
    const loadMasters = async () => {
      try {
        setLoadingUoms(true);

        // Load UOMs
        const res = await axiosClient.get("/api/ppe/masters/uoms");
        setUoms(res.data.data || []);

        // ------------------------------------------------------------
        // ADDED: Load Tax Classifications
        // ------------------------------------------------------------
        const taxRes = await axiosClient.get(
          "/api/ppe/masters/tax-classifications"
        );
        setTaxList(taxRes.data.data || []);
      } catch (err) {
        console.error("Failed to load masters", err);
      } finally {
        setLoadingUoms(false);
      }
    };

    if (open) {
      loadMasters();
    }
  }, [open]);

  // ------------------------------------------------------------
  // CHANGED: Initialize tax field in edit mode
  // ------------------------------------------------------------
  useEffect(() => {
    if (mode === "edit" && record) {
      setForm({
        code: record.code || "",
        name: record.name || "",
        base_uom_id: record.base_uom_id || "",
        issue_uom_id: record.issue_uom_id || "",
        supplier_reference: record.supplier_reference || "",

        // ------------------------------------------------------------
        // ADDED: Load existing tax classification for edit
        // ------------------------------------------------------------
        tax_classification_id: record.tax_classification_id || "",
      });
    } else {
      setForm({
        code: "",
        name: "",
        base_uom_id: "",
        issue_uom_id: "",
        supplier_reference: "",

        // ------------------------------------------------------------
        // ADDED: Default empty tax classification
        // ------------------------------------------------------------
        tax_classification_id: "",
      });
    }
  }, [mode, record]);

  if (!open) return null;

  const onChange = (field, value) => {
    setError(null);

    setForm((p) => {
      const updated = { ...p, [field]: value };

      // Auto-set issue UOM when base UOM changes
      if (field === "base_uom_id" && !p.issue_uom_id) {
        updated.issue_uom_id = value;
      }

      return updated;
    });
  };

  // ------------------------------------------------------------
  // Submit handler
  // ------------------------------------------------------------
  const onSubmit = async () => {
    setError(null);

    // ------------------------------------------------------------
    // CHANGED: Validation includes tax classification
    // ------------------------------------------------------------
    if (!form.code || !form.name || !form.base_uom_id || !form.tax_classification_id) {
      setError("Code, Name, Base UOM and Tax Classification are mandatory");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code: form.code,
        name: form.name,

        base_uom_id: parseInt(form.base_uom_id),
        issue_uom_id: parseInt(
          form.issue_uom_id || form.base_uom_id
        ),

        supplier_reference: form.supplier_reference || undefined,

        // ------------------------------------------------------------
        // ADDED: Send tax classification to backend
        // ------------------------------------------------------------
        tax_classification_id: parseInt(form.tax_classification_id),
      };

      if (mode === "create") {
        await axiosClient.post(
          "/api/ppe/masters/boughtout-items",
          payload
        );
      }

      if (mode === "edit" && record?.id) {
        await axiosClient.put(
          `/api/ppe/masters/boughtout-items/${record.id}`,
          payload
        );
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 420,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {mode === "create"
            ? "Create Bought-Out Item"
            : "Edit Bought-Out Item"}
        </h6>
        <button className="btn btn-sm btn-light" onClick={onClose}>
          ✕
        </button>
      </div>

      <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mb-3">
          <label className="form-label">Code *</label>
          <input
            className="form-control"
            value={form.code}
            disabled={mode === "edit"}
            onChange={(e) => onChange("code", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Name *</label>
          <input
            className="form-control"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Base UOM *</label>
          <select
            className="form-select"
            value={form.base_uom_id}
            disabled={loadingUoms}
            onChange={(e) => onChange("base_uom_id", e.target.value)}
          >
            <option value="">
              {loadingUoms ? "Loading..." : "-- Select Base UOM --"}
            </option>
            {uoms.map((u) => (
              <option key={u.id} value={u.id}>
                {u.uom_code} - {u.uom_name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label className="form-label">Issue UOM</label>
          <select
            className="form-select"
            value={form.issue_uom_id}
            disabled={loadingUoms}
            onChange={(e) => onChange("issue_uom_id", e.target.value)}
          >
            <option value="">-- Select Issue UOM --</option>
            {uoms.map((u) => (
              <option key={u.id} value={u.id}>
                {u.uom_code} - {u.uom_name}
              </option>
            ))}
          </select>
        </div>

        {/* ------------------------------------------------------------
            ADDED: Tax Classification Dropdown
        ------------------------------------------------------------ */}
        <div className="mb-3">
          <label className="form-label">Tax Classification *</label>
          <select
            className="form-select"
            value={form.tax_classification_id}
            onChange={(e) =>
              onChange("tax_classification_id", e.target.value)
            }
          >
            <option value="">-- Select HSN / SAC --</option>

            {taxList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.tax_system} - {t.tax_code} ({t.description})
              </option>
            ))}
          </select>
        </div>
        {/* ------------------------------------------------------------ */}

        <div className="mb-3">
          <label className="form-label">Supplier Reference</label>
          <input
            className="form-control"
            value={form.supplier_reference}
            onChange={(e) =>
              onChange("supplier_reference", e.target.value)
            }
          />
        </div>
      </div>

      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button className="btn btn-outline-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={onSubmit}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
